/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, Ref } from 'preact';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'preact/hooks';

import {
  type DropdownList,
  isBeforeDataFetch,
  isPhone,
  isSearchTextEmptyOrUndefined,
  useCollectionRenderer,
  UseCollectionRendererSelectSingleParams,
  useSelectCommon
} from '#PRIVATE_SelectCommon';
import type { Label } from '#UNSAFE_Label';
import type { SelectSingleField } from '#UNSAFE_SelectSingle/SelectSingleField';
import type { SelectSingleFieldInput } from '#UNSAFE_SelectSingle/SelectSingleFieldInput';
import { useDisplayValue } from '#UNSAFE_SelectSingle/useDisplayValue';
import type { InlineUserAssistance } from '#UNSAFE_UserAssistance';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useLoadingIndicatorTimer } from '#hooks/UNSAFE_useLoadingIndicatorTimer';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import { isControlOrFunctionKey, KEYS } from '#utils/PRIVATE_keyboardUtils';
import { allTabbableElements } from '#utils/PRIVATE_tabbableUtils';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import type {
  CommitMetadata,
  Data,
  InlineSelectHandle,
  InlineSelectSingleProps,
  Key
} from './types';
import { UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';

type UseInlineSelectSingleOptions<K extends Key, D extends Data> = InlineSelectSingleProps<K, D>;

/**
 * A custom hook for determining prop values for the InlineSelectSingle's child
 * components from its own props.
 * @param param0 options
 * @param ref The ref handle of the component.
 */
export function useInlineSelectSingle<K extends Key, D extends Data>(
  {
    'aria-describedby': ariaDescribedBy,
    assistiveText,
    data,
    hasBackIcon = 'never',
    hasClearIcon = 'never',
    helpSourceLink,
    helpSourceText,
    isLoading: propIsLoading,
    itemText,
    label,
    onBackIconAction,
    onCommit: propOnCommit,
    onFilter,
    placeholder,
    textAlign: propTextAlign,
    valueItem,
    virtualKeyboard
  }: UseInlineSelectSingleOptions<K, D>,
  ref: Ref<InlineSelectHandle>
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { textAlign: formTextAlign } = useFormContext();
  const textAlign = propTextAlign ?? formTextAlign ?? 'start';
  const isDataFetched = data != null && !isBeforeDataFetch<K, D>(data);
  const isEmptyResults = isDataFetched && data?.totalSize === 0;

  // Refs to keep track of prop changes and sync internal state
  const prevDataRef = useRef(data);
  const prevValueItemRef = useRef(valueItem);
  const isInitialRender = useRef(true);

  // State for holding the selected keys in the list
  const [selectedKeys, setSelectedKeys] = useState(
    valueItem === undefined ? undefined : new Set([valueItem.key])
  );

  // The incoming propIsLoading tells us that we are in a loading state, but we don't want to
  // show the loading indicator until after a delay, because showing it immediately could result
  // in unwanted flashing.  The isLoading var below will be set to true by useLoadingIndicatorTimer
  // after the delay, when the timer expires, at which point we should show the loading indicator.
  // If propIsLoading is set to false before the timer expires, then the timer will be cancelled
  // and no loading indicator will be shown.
  // (ListView detects its loading state by checking data === null.  While it would be nice for
  // us to be consistent and check valueItems === null, we need to use a separate isLoading prop.
  // If valueItems === null triggers our loading state, then the selections in the dropdown
  // are cleared for that period of time, which is undesirable.)
  const isLoading = useLoadingIndicatorTimer(propIsLoading ?? false);
  const displayValue = useDisplayValue(itemText, valueItem, 'InlineSelectSingle');

  // add methods to the ref
  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      [UNSAFE_DOM_ACCESS]: inputRef.current!
    }),
    []
  );

  const {
    currentRowKeyRef,
    currentRowOverride,
    dropdownRef,
    isFocused,
    isUserFiltering,
    onCurrentKeyChange,
    searchText,
    setCurrentRowOverride,
    stopFiltering,
    updateFiltering,
    userInput
  } = useSelectCommon<K, D>({
    data,
    inputRef,
    // The component can only be in a focused visual state
    isFocused: true,
    onFilter
  });

  const { baseId, formFieldContext, inputProps, labelProps, userAssistanceProps } = useTextField({
    ariaDescribedBy,
    helpSourceLink,
    helpSourceText,
    isFocused,
    isLoading,
    value: valueItem !== undefined ? true : undefined
  });
  const listId = `${baseId}-list`;

  // If we're waiting for the selected values to load, the component should look like it
  // has a value in the meantime.
  // Use the original propIsLoading instead of isLoading because we don't want to delay
  // the inside label position by a timer like we do the progressive loading indicator.
  formFieldContext.hasValue = propIsLoading || formFieldContext.hasValue;

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const liveRegionText = isDataFetched
    ? data?.totalSize === 0
      ? translations.select_noMatchesFound()
      : data?.totalSize === 1
      ? translations.select_oneMatchFound()
      : data?.sizePrecision === 'exact'
      ? translations.select_sizeMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
      : translations.select_sizeOrMoreMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
    : '';

  /**
   * Lifecycle updates
   */
  // When the valueItem changes, save the new selectedKeys
  if (prevValueItemRef.current !== valueItem) {
    prevValueItemRef.current = valueItem;
    setSelectedKeys(valueItem === undefined ? undefined : new Set([valueItem.key]));
  }

  // When data changes, highlight the first result
  if (prevDataRef.current !== data) {
    prevDataRef.current = data;
    // when filtering, set the first result of the filtered data set to be the current item
    // (check the data offset so we don't try to highlight again if the user scrolls through
    // the list and more data is fetched)
    if (isUserFiltering && data != null && data.offset === 0 && data.data.length > 0) {
      // if there are results and search text, highlight the first row, otherwise clear
      // an existing highlight
      setCurrentRowOverride({
        rowKey: !isSearchTextEmptyOrUndefined(searchText) ? data.data[0].metadata.key : undefined
      });
    }
  }

  // During the initial render, set the current row override if we have selected values.
  // This way pressing DOWN arrow will start the navigation from the selected row.
  if (isInitialRender.current) {
    isInitialRender.current = false;
    setCurrentRowOverride({ rowKey: [...(selectedKeys ?? [undefined])][0] });
  }

  /**
   * A helper onCommit wrapper that consumes the metadata and calls the
   * propOnCommit with only the necessary information.
   * (Similar to onCommit in useSelectSingle.ts)
   */
  const onCommit = useCallback(
    (value: K | undefined, metadata: CommitMetadata) => {
      propOnCommit?.({
        value: value,
        previousValue: valueItem !== undefined ? valueItem.key : undefined,
        reason: metadata.reason
      });
    },
    [propOnCommit, valueItem]
  );

  /**
   * A helper function to clear the selectedKeys and commits empty value.
   * (Similar to clearValue in useSelectSingle.ts but without dropdown related logic)
   */
  const clearValue = useCallback(
    (metadata: CommitMetadata) => {
      setSelectedKeys(new Set<K>());
      // call the onCommit callback so that the consuming code can update the valueItem
      onCommit(undefined, metadata);
    },
    [onCommit]
  );

  /**
   * Determine if we need to clear the value because the user has deleted all the text.
   * (Similar to isClearValueNeeded in useSelectSingle.ts)
   */
  const isClearValueNeeded = useCallback(() => {
    return isUserFiltering && userInput === '' && valueItem !== undefined;
  }, [isUserFiltering, userInput, valueItem]);

  /**
   * Handler for List.onItemAction
   * (Similar to useSelectSingle.ts's onItemAction but without dropdown related logic)
   * This is used for handling:
   * - Commits value when one performs an action on the list
   */
  const handleItemAction = useCallback<
    UseCollectionRendererSelectSingleParams<K, D>['onItemAction']
  >(
    (detail) => {
      // after selecting a value, the user is no longer actively filtering, so update related
      // state accordingly so that the newly selected value appears in the field
      stopFiltering();

      const newKey = detail.context.key;
      // update the selectedKeys so that the selection visual state of the collection
      // updates immediately while waiting for a new valueItem to be set
      setSelectedKeys(new Set([newKey]));
      // call the onCommit callback so that the consuming code can update the valueItem
      onCommit(newKey, { reason: 'itemAction' });
    },
    [onCommit, stopFiltering]
  );

  /**
   * Handler for SelectSingleFieldInput.onInput
   * This is used for handling:
   * - Typing in text in the field should filter the results (i.e. invoke onFilter with the text)
   */
  const handleFieldInput = useCallback(
    ({ value }: ValueUpdateDetail<string>) => {
      updateFiltering(value);
      onFilter?.({ searchText: value });
    },
    [onFilter, updateFiltering]
  );

  /**
   * Handler for SelectSingleFieldInput.onKeyDown
   * This is used for handling:
   * - Prevents default browser behavior on UP/DOWN to avoid scrolling
   * - Clears value or selects current row on ENTER
   * - Clears value or selects current row on TAB
   */
  const handleFieldKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // ignore ctrl and fn key events
      if (isControlOrFunctionKey(event)) return;

      switch (event.code) {
        case KEYS.UP:
        case KEYS.DOWN:
          // prevent the default action, since we do not want the page to scroll
          event.preventDefault();
          break;
        case KEYS.ENTER:
        case KEYS.TAB: {
          // if this is on a mobile device (only phone and not in tablet devices), do not do anything
          if (isPhone()) return;

          // Otherwise, proceed
          const commitMetadata: CommitMetadata = {
            reason: event.code === KEYS.TAB ? 'tabKey' : 'itemAction'
          };
          // check to see if we need to clear the value
          if (isClearValueNeeded()) {
            // in this case, clear the value and return.
            clearValue(commitMetadata);
            return;
          }

          // if there is no data to select, no need to handle this event
          if (data == null || data.data.length === 0) return;

          // If we will be making a selection, the user is no longer actively filtering,
          // so update related state accordingly so that the newly selected value appears in the field.
          stopFiltering();

          // Current design have this component in a dropdown/modal. So, pressing Tab from
          // the main field without doing any other interaction is still considered a selection
          // unlike SelectSingle (see JET-68215). So, we handle both Enter and Tab similarly.
          // If there is a currentRow in the dropdown (meaning a highlighted item that would
          // have keyboard focus if the dropdown list had physical focus, which may be different
          // from the currently selected item), select it and commit it.
          if (currentRowKeyRef.current !== undefined && data != null && data.data.length > 0) {
            setSelectedKeys(new Set<K>([currentRowKeyRef.current]));
            onCommit(currentRowKeyRef.current, commitMetadata);
            return;
          }
          // Otherwise commit the currently selected value again so that a valueAction event
          // can be fired from the core-pack side.
          onCommit(valueItem?.key ?? undefined, commitMetadata);
          break;
        }
      }
    },
    [clearValue, currentRowKeyRef, data, isClearValueNeeded, onCommit, stopFiltering, valueItem]
  );

  /**
   * Handler for SelectSingleFieldInput.onKeyUp
   * This is used for handling:
   * - Pressing UP/DOWN from the main field should move the focus to the list
   * - If Ctrl or Fn key is pressed, then ignore the event
   */
  const handleFieldKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // ignore ctrl and fn key events
      if (isControlOrFunctionKey(event)) return;

      switch (event.code) {
        case KEYS.UP:
        case KEYS.DOWN:
          // Transfer physical focus into the dropdown only if the data is present and the dropdown
          // has rendered the data. Otherwise, keep the focus in the field.
          if (data != null && dropdownRef.current) {
            // get the focusable element from the dropdown.
            // consider element with tabindex=-1 as well.
            const firstFocusableElem = allTabbableElements(dropdownRef.current, undefined, true)[0];
            // focus the first element if it exists
            firstFocusableElem?.focus();

            // set the first row as current row if there is no current row at the moment
            if (currentRowOverride.rowKey === undefined) {
              setCurrentRowOverride({ rowKey: data.data[0]?.metadata?.key });
            }
          }
          // prevent the default action, since we are handling it
          event.preventDefault();
          break;
      }
    },
    [currentRowOverride, data, dropdownRef, setCurrentRowOverride]
  );

  /**
   * Handler for DropdownList.onKeyDown
   * This is used for handling:
   * - Pressing Tab when highlighted on a list item should select & commit that item as value
   */
  const handleListKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case KEYS.TAB:
          // if there is a currentRow in the dropdown, select it and commit it
          if (currentRowKeyRef.current !== undefined && data != null && data.data.length > 0) {
            setSelectedKeys(new Set<K>([currentRowKeyRef.current]));
            onCommit(currentRowKeyRef.current, { reason: 'tabKey' });
          }
          break;
      }
    },
    [currentRowKeyRef, data, onCommit]
  );

  /**
   * Handler for ClearButton.onClick
   * This is for handling:
   * - on clicking the clear icon, the value & selection should be cleared.
   */
  const handleClearButtonClick = useCallback(() => {
    // reset the user filter and the value of the component
    stopFiltering();
    // clear the value
    clearValue({ reason: 'itemAction' });
    // set focus to the input
    inputRef.current?.focus();
    // call onFilter with undefined
    onFilter?.({ searchText: undefined });
  }, [clearValue, onFilter, stopFiltering]);

  /**
   * Create collection Renderer context. This will be used by the default
   * collectionRenderer.
   */
  const collectionRendererContext = useCollectionRenderer<K, D>({
    currentKey: currentRowOverride.rowKey,
    searchText,
    selectedKeys,
    onCurrentKeyChange,
    onItemAction: handleItemAction
  });

  // TODO: JET-73973 - ListView is not respecting TabbableMode
  // Currently List does not respect TabbableMode, so for now explicitly set tabindex="-1".
  // When this bug is fixed, this useEffect can be removed.
  useEffect(() => {
    const list = dropdownRef.current?.querySelector('div[role="listbox"][tabindex="0"]');
    list?.setAttribute('tabindex', '-1');
  });

  return {
    collectionRendererContext,
    formFieldContext,
    hasUserAssistance: Boolean(assistiveText || helpSourceLink),
    isEmptyResults,
    isUserFiltering,
    labelProps: {
      ...labelProps,
      children: label
    } satisfies ComponentProps<typeof Label>,
    listContainerProps: {
      id: listId,
      dropdownListRef: dropdownRef,
      onKeyDown: handleListKeyDown
    } satisfies ComponentProps<typeof DropdownList>,
    selectSingleFieldProps: {
      contentVariant: 'input',
      // InlineSelectSingle currently only supports inside label.
      hasInsideLabel: true,
      isBackButtonShown: hasBackIcon === 'always',
      isClearButtonShown: hasClearIcon === 'conditionally' && valueItem !== undefined,
      onBackButtonClick: onBackIconAction,
      onClearButtonClick: handleClearButtonClick,
      styleVariant: 'embedded' as const
    } satisfies Omit<ComponentProps<typeof SelectSingleField>, 'children'>,
    selectFieldInputProps: {
      ...inputProps,
      'aria-controls': listId,
      // InlineSelectSingle always have the list open, so it will always be in an expanded state.
      'aria-expanded': true,
      displayValue,
      // InlineSelectSingle currently only supports inside label,
      // so it is expected to always have a label.
      hasEmptyLabel: false,
      // InlineSelectSingle currently only supports inside label.
      hasInsideLabel: true,
      inputRef,
      // InlineSelectSingle currently does not support Add to List.
      isAddToListShown: false,
      // InlineSelectSingle currently does not support Advanced Search.
      isAdvancedSearchShown: false,
      // InlineSelectSingle currently does not support Required.
      isRequired: false,
      isUserFiltering,
      liveRegionText,
      onInput: handleFieldInput,
      onKeyDown: handleFieldKeyDown,
      onKeyUp: handleFieldKeyUp,
      placeholder,
      textAlign,
      userInput,
      variant: 'embedded',
      virtualKeyboard
    } satisfies ComponentProps<typeof SelectSingleFieldInput>,
    userAssistanceProps: {
      ...userAssistanceProps,
      assistiveText,
      fieldLabel: label,
      helpSourceLink,
      helpSourceText
    } satisfies ComponentProps<typeof InlineUserAssistance>
  };
}
